
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import  themen  from 'theme/styles/styled-colors';
import  * as theme  from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "fault-tolerance-and-high-availability-options"
    }}>{`Fault Tolerance and High Availability Options`}</h1>
    <h2 {...{
      "id": "introduction"
    }}>{`Introduction`}</h2>
    <p>{`Apache Atlas uses and interacts with a variety of systems to provide metadata management and data lineage to data
administrators. By choosing and configuring these dependencies appropriately, it is possible to achieve a high degree
of service availability with Atlas. This document describes the state of high availability support in Atlas,
including its capabilities and current limitations, and also the configuration required for achieving this level of
high availability.`}</p>
    <p><a parentName="p" {...{
        "href": "#/Architecture"
      }}>{`The architecture page`}</a>{` in the wiki gives an overview of the various components that make up Atlas.
The options mentioned below for various components derive context from the above page, and would be worthwhile to
review before proceeding to read this page.`}</p>
    <h2 {...{
      "id": "atlas-web-service"
    }}>{`Atlas Web Service`}</h2>
    <p>{`Currently, the Atlas Web Service has a limitation that it can only have one active instance at a time. In earlier
releases of Atlas, a backup instance could be provisioned and kept available. However, a manual failover was
required to make this backup instance active.`}</p>
    <p>{`From this release, Atlas will support multiple instances of the Atlas Web service in an active/passive configuration
with automated failover. This means that users can deploy and start multiple instances of the Atlas Web Service on
different physical hosts at the same time. One of these instances will be automatically selected as an 'active'
instance to service user requests. The others will automatically be deemed 'passive'. If the 'active' instance
becomes unavailable either because it is deliberately stopped, or due to unexpected failures, one of the other
instances will automatically be elected as an 'active' instance and start to service user requests.`}</p>
    <p>{`An 'active' instance is the only instance that can respond to user requests correctly. It can create, delete, modify
or respond to queries on metadata objects. A 'passive' instance will accept user requests, but will redirect them
using HTTP redirect to the currently known 'active' instance. Specifically, a passive instance will not itself
respond to any queries on metadata objects. However, all instances (both active and passive), will respond to admin
requests that return information about that instance.`}</p>
    <p>{`When configured in a High Availability mode, users can get the following operational benefits:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Uninterrupted service during maintenance intervals`}</strong>{`: If an active instance of the Atlas Web Service needs to be brought down for maintenance, another instance would automatically become active and can service requests.`}</li>
      <li parentName="ul"><strong parentName="li">{`Uninterrupted service in event of unexpected failures`}</strong>{`: If an active instance of the Atlas Web Service fails due to software or hardware errors, another instance would automatically become active and can service requests.`}</li>
    </ul>
    <p>{`In the following sub-sections, we describe the steps required to setup High Availability for the Atlas Web Service.
We also describe how the deployment and client can be designed to take advantage of this capability.
Finally, we describe a few details of the underlying implementation.`}</p>
    <h3 {...{
      "id": "setting-up-the-high-availability-feature-in-atlas"
    }}>{`Setting up the High Availability feature in Atlas`}</h3>
    <p>{`The following pre-requisites must be met for setting up the High Availability feature.`}</p>
    <ul>
      <li parentName="ul">{`Ensure that you install Apache Zookeeper on a cluster of machines (a minimum of 3 servers is recommended for production).`}</li>
      <li parentName="ul">{`Select 2 or more physical machines to run the Atlas Web Service instances on. These machines define what we refer to as a 'server ensemble' for Atlas.`}</li>
    </ul>
    <p>{`To setup High Availability in Atlas, a few configuration options must be defined in the `}<inlineCode parentName="p">{`atlas-application.properties`}</inlineCode>{`
file. While the complete list of configuration items are defined in the `}<a parentName="p" {...{
        "href": "#/Configuration"
      }}>{`Configuration Page`}</a>{`, this
section lists a few of the main options.`}</p>
    <ul>
      <li parentName="ul">{`High Availability is an optional feature in Atlas. Hence, it must be enabled by setting the configuration option `}<inlineCode parentName="li">{`atlas.server.ha.enabled`}</inlineCode>{` to true.`}</li>
    </ul>
    <ul>
      <li parentName="ul">{`Next, define a list of identifiers, one for each physical machine you have selected for the Atlas Web Service instance. These identifiers can be simple strings like `}<inlineCode parentName="li">{`id1`}</inlineCode>{`, `}<inlineCode parentName="li">{`id2`}</inlineCode>{` etc. They should be unique and should not contain a comma.`}</li>
    </ul>
    <ul>
      <li parentName="ul">{`Define a comma separated list of these identifiers as the value of the option `}<inlineCode parentName="li">{`atlas.server.ids`}</inlineCode>{`.`}</li>
    </ul>
    <ul>
      <li parentName="ul">{`For each physical machine, list the IP Address/hostname and port as the value of the configuration `}<inlineCode parentName="li">{`atlas.server.address.id`}</inlineCode>{`, where `}<inlineCode parentName="li">{`id`}</inlineCode>{` refers to the identifier string for this physical machine.`}
        <ul parentName="li">
          <li parentName="ul">{`For e.g., if you have selected 2 machines with hostnames `}<inlineCode parentName="li">{`host1.company.com`}</inlineCode>{` and `}<inlineCode parentName="li">{`host2.company.com`}</inlineCode>{`, you can define the configuration options as below:`}</li>
        </ul>
      </li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
      {`atlas.server.ids=id1,id2
atlas.server.address.id1=host1.company.com:21000
atlas.server.address.id2=host2.company.com:21000`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`Define the Zookeeper quorum which will be used by the Atlas High Availability feature.`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
      atlas.server.ha.zookeeper.connect=zk1.company.com:2181,zk2.company.com:2181,zk3.company.com:2181
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`You can review other configuration options that are defined for the High Availability feature, and set them up as desired in the `}<inlineCode parentName="li">{`atlas-application.properties`}</inlineCode>{` file.`}</li>
      <li parentName="ul">{`For production environments, the components that Atlas depends on must also be set up in High Availability mode. This is described in detail in the following sections. Follow those instructions to setup and configure them.`}</li>
      <li parentName="ul">{`Install the Atlas software on the selected physical machines.`}</li>
      <li parentName="ul">{`Copy the `}<inlineCode parentName="li">{`atlas-application.properties`}</inlineCode>{` file created using the steps above to the configuration directory of all the machines.`}</li>
      <li parentName="ul">{`Start the dependent components.`}</li>
      <li parentName="ul">{`Start each instance of the Atlas Web Service.`}</li>
    </ul>
    <p>{`To verify that High Availability is working, run the following script on each of the instances where Atlas Web Service
is installed.`}</p>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
$ATLAS_HOME/bin/atlas_admin.py -status
    </SyntaxHighlighter>
This script can print one of the values below as response:
    <ul>
      <li parentName="ul"><strong parentName="li">{`ACTIVE`}</strong>{`: This instance is active and can respond to user requests.`}</li>
      <li parentName="ul"><strong parentName="li">{`PASSIVE`}</strong>{`: This instance is PASSIVE. It will redirect any user requests it receives to the current active instance.`}</li>
      <li parentName="ul"><strong parentName="li">{`BECOMING_ACTIVE`}</strong>{`: This would be printed if the server is transitioning to become an ACTIVE instance. The server cannot service any metadata user requests in this state.`}</li>
      <li parentName="ul"><strong parentName="li">{`BECOMING_PASSIVE`}</strong>{`: This would be printed if the server is transitioning to become a PASSIVE instance. The server cannot service any metadata user requests in this state.`}</li>
    </ul>
    <p>{`Under normal operating circumstances, only one of these instances should print the value `}<em parentName="p">{`ACTIVE`}</em>{` as response to
the script, and the others would print `}<em parentName="p">{`PASSIVE`}</em>{`.`}</p>
    <h3 {...{
      "id": "configuring-clients-to-use-the-high-availability-feature"
    }}>{`Configuring clients to use the High Availability feature`}</h3>
    <p>{`The Atlas Web Service can be accessed in two ways:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Using the Atlas Web UI`}</strong>{`: This is a browser based client that can be used to query the metadata stored in Atlas.`}</li>
      <li parentName="ul"><strong parentName="li">{`Using the Atlas REST API`}</strong>{`: As Atlas exposes a RESTful API, one can use any standard REST client including libraries in other applications. In fact, Atlas ships with a client called AtlasClient that can be used as an example to build REST client access.`}</li>
    </ul>
    <p>{`In order to take advantage of the High Availability feature in the clients, there are two options possible.`}</p>
    <h4 {...{
      "id": "using-an-intermediate-proxy"
    }}>{`Using an intermediate proxy`}</h4>
    <p>{`The simplest solution to enable highly available access to Atlas is to install and configure some intermediate proxy
that has a capability to transparently switch services based on status. One such proxy solution is `}<a parentName="p" {...{
        "href": "http://www.haproxy.org/"
      }}>{`HAProxy`}</a>{`.`}</p>
    <p>{`Here is an example HAProxy configuration that can be used. Note this is provided for illustration only, and not as a
recommended production configuration. For that, please refer to the HAProxy documentation for appropriate instructions.`}</p>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
      {`frontend atlas_fe
  bind *:41000
  default_backend atlas_be
backend atlas_be
  mode http
  option httpchk get /api/atlas/admin/status
  http-check expect string ACTIVE
  balance roundrobin
  server host1_21000 host1:21000 check
  server host2_21000 host2:21000 check backup
listen atlas
  bind localhost:42000`}
    </SyntaxHighlighter>
    <p>{`The above configuration binds HAProxy to listen on port 41000 for incoming client connections. It then routes
the connections to either of the hosts host1 or host2 depending on a HTTP status check. The status check is
done using a HTTP GET on the REST URL `}<inlineCode parentName="p">{`/api/atlas/admin/status`}</inlineCode>{`, and is deemed successful only if the HTTP response
contains the string ACTIVE.`}</p>
    <h4 {...{
      "id": "using-automatic-detection-of-active-instance"
    }}>{`Using automatic detection of active instance`}</h4>
    <p>{`If one does not want to setup and manage a separate proxy, then the other option to use the High Availability
feature is to build a client application that is capable of detecting status and retrying operations. In such a
setting, the client application can be launched with the URLs of all Atlas Web Service instances that form the
ensemble. The client should then call the REST URL `}<inlineCode parentName="p">{`/api/atlas/admin/status`}</inlineCode>{` on each of these to determine which is
the active instance. The response from the Active instance would be of the form `}<inlineCode parentName="p">{`{Status:ACTIVE}`}</inlineCode>{`. Also, when the
client faces any exceptions in the course of an operation, it should again determine which of the remaining URLs
is active and retry the operation.`}</p>
    <p>{`The AtlasClient class that ships with Atlas can be used as an example client library that implements the logic
for working with an ensemble and selecting the right Active server instance.`}</p>
    <p>{`Utilities in Atlas, like `}<inlineCode parentName="p">{`quick_start.py`}</inlineCode>{` and `}<inlineCode parentName="p">{`import-hive.sh`}</inlineCode>{` can be configured to run with multiple server
URLs. When launched in this mode, the AtlasClient automatically selects and works with the current active instance.
If a proxy is set up in between, then its address can be used when running quick_start.py or import-hive.sh.`}</p>
    <h3 {...{
      "id": "implementation-details-of-atlas-high-availability"
    }}>{`Implementation Details of Atlas High Availability`}</h3>
    <p>{`The Atlas High Availability work is tracked under the master JIRA
`}<a parentName="p" {...{
        "href": "https://issues.apache.org/jira/browse/ATLAS-510"
      }}>{`ATLAS-510`}</a>{`.
The JIRAs filed under it have detailed information about how the High Availability feature has been implemented.
At a high level the following points can be called out:`}</p>
    <ul>
      <li parentName="ul">{`The automatic selection of an Active instance, as well as automatic failover to a new Active instance happen through a leader election algorithm.`}</li>
      <li parentName="ul">{`For leader election, we use the `}<a parentName="li" {...{
          "href": "http://curator.apache.org/curator-recipes/leader-latch.html"
        }}>{`Leader Latch Recipe`}</a>{` of `}<a parentName="li" {...{
          "href": "http://curator.apache.org"
        }}>{`Apache Curator`}</a></li>
      <li parentName="ul">{`The Active instance is the only one which initializes, modifies or reads state in the backend stores to keep them consistent.`}</li>
      <li parentName="ul">{`Also, when an instance is elected as Active, it refreshes any cached information from the backend stores to get up to date.`}</li>
      <li parentName="ul">{`A servlet filter ensures that only the active instance services user requests. If a passive instance receives these requests, it automatically redirects them to the current active instance.`}</li>
    </ul>
    <h2 {...{
      "id": "metadata-store"
    }}>{`Metadata Store`}</h2>
    <p>{`As described above, Atlas uses JanusGraph to store the metadata it manages. By default, Atlas uses a standalone HBase
instance as the backing store for JanusGraph. In order to provide HA for the metadata store, we recommend that Atlas be
configured to use distributed HBase as the backing store for JanusGraph.  Doing this implies that you could benefit from the
HA guarantees HBase provides. In order to configure Atlas to use HBase in HA mode, do the following:`}</p>
    <ul>
      <li parentName="ul">{`Choose an existing HBase cluster that is set up in HA mode to configure in Atlas (OR) Set up a new HBase cluster in `}<a parentName="li" {...{
          "href": "http://hbase.apache.org/book.html#quickstart_fully_distributed"
        }}>{`HA mode`}</a>{`.`}
        <ul parentName="li">
          <li parentName="ul">{`If setting up HBase for Atlas, please following instructions listed for setting up HBase in the `}<a parentName="li" {...{
              "href": "#/Installation"
            }}>{`Installation Steps`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`We recommend using more than one HBase masters (at least 2) in the cluster on different physical hosts that use Zookeeper for coordination to provide redundancy and high availability of HBase.`}
        <ul parentName="li">
          <li parentName="ul">{`Refer to the `}<a parentName="li" {...{
              "href": "#/Configuration"
            }}>{`Configuration page`}</a>{` for the options to configure in atlas.properties to setup Atlas with HBase.`}</li>
        </ul>
      </li>
    </ul>
    <h2 {...{
      "id": "index-store"
    }}>{`Index Store`}</h2>
    <p>{`As described above, Atlas indexes metadata through JanusGraph to support full text search queries. In order to provide HA
for the index store, we recommend that Atlas be configured to use Solr or Elasticsearch as the backing index store for JanusGraph.`}</p>
    <h3 {...{
      "id": "solr"
    }}>{`Solr`}</h3>
    <p>{`In order to configure Atlas to use Solr in HA mode, do the following:`}</p>
    <ul>
      <li parentName="ul">{`Choose an existing SolrCloud cluster setup in HA mode to configure in Atlas (OR) Set up a new `}<a parentName="li" {...{
          "href": "https://cwiki.apache.org/confluence/display/solr/SolrCloud"
        }}>{`SolrCloud cluster`}</a>{`.`}
        <ul parentName="li">
          <li parentName="ul">{`Ensure Solr is brought up on at least 2 physical hosts for redundancy, and each host runs a Solr node.`}</li>
          <li parentName="ul">{`We recommend the number of replicas to be set to at least 2 for redundancy.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Create the SolrCloud collections required by Atlas, as described in `}<a parentName="li" {...{
          "href": "#/Installation"
        }}>{`Installation Steps`}</a></li>
      <li parentName="ul">{`Refer to the `}<a parentName="li" {...{
          "href": "#/Configuration"
        }}>{`Configuration page`}</a>{` for the options to configure in atlas.properties to setup Atlas with Solr.`}</li>
    </ul>
    <h3 {...{
      "id": "elasticsearch--tech-preview"
    }}>{`Elasticsearch  (Tech Preview)`}</h3>
    <p>{`In order to configure Atlas to use Elasticsearch in HA mode, do the following:`}</p>
    <ul>
      <li parentName="ul">{`Choose an existing Elasticsearch cluster setup, (OR) setup a new cluster `}<a parentName="li" {...{
          "href": "https://www.elastic.co/guide/en/elasticsearch/reference/5.6/setup.html"
        }}>{`Elasticsearch cluster`}</a>{`.`}
        <ul parentName="li">
          <li parentName="ul">{`Ensure that Elasticsearch is brought up on at least five physical hosts for redundancy.`}</li>
          <li parentName="ul">{`A replica count of 3 is recommended`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Refer to the `}<a parentName="li" {...{
          "href": "#/Configuration"
        }}>{`Configuration page`}</a>{` for the options to configure in atlas.properties to setup Atlas with Elasticsearch.`}</li>
    </ul>
    <h2 {...{
      "id": "notification-server"
    }}>{`Notification Server`}</h2>
    <p>{`Metadata notification events from Hooks are sent to Atlas by writing them to a Kafka topic called `}<strong parentName="p">{`ATLAS_HOOK`}</strong>{`. Similarly, events from
Atlas to other integrating components like Ranger, are written to a Kafka topic called `}<strong parentName="p">{`ATLAS_ENTITIES`}</strong>{`. Since Kafka
persists these messages, the events will not be lost even if the consumers are down as the events are being sent. In
addition, we recommend Kafka is also setup for fault tolerance so that it has higher availability guarantees. In order
to configure Atlas to use Kafka in HA mode, do the following:`}</p>
    <ul>
      <li parentName="ul">{`Choose an existing Kafka cluster set up in HA mode to configure in Atlas (OR) Set up a new Kafka cluster.`}</li>
    </ul>
    <ul>
      <li parentName="ul">{`We recommend that there are more than one Kafka brokers in the cluster on different physical hosts that use Zookeeper for coordination to provide redundancy and high availability of Kafka.`}
        <ul parentName="li">
          <li parentName="ul">{`Setup at least 2 physical hosts for redundancy, each hosting a Kafka broker.`}</li>
        </ul>
      </li>
    </ul>
    <ul>
      <li parentName="ul">{`Set up Kafka topics for Atlas usage:`}
        <ul parentName="li">
          <li parentName="ul">{`The number of partitions for the ATLAS topics should be set to 1 (numPartitions)`}</li>
          <li parentName="ul">{`Decide number of replicas for Kafka topic: Set this to at least 2 for redundancy.`}</li>
          <li parentName="ul">{`Run the following commands:`}</li>
        </ul>
      </li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
   {`$KAFKA_HOME/bin/kafka-topics.sh --create --zookeeper <list of zookeeper host:port entries> --topic ATLAS_HOOK --replication-factor <numReplicas> --partitions 1
   $KAFKA_HOME/bin/kafka-topics.sh --create --zookeeper <list of zookeeper host:port entries> --topic ATLAS_ENTITIES --replication-factor <numReplicas> --partitions 1
   Here KAFKA_HOME points to the Kafka installation directory.`}
    </SyntaxHighlighter>
    <ul>
      <li parentName="ul">{`In atlas-application.properties, set the following configuration:`}</li>
    </ul>
    <SyntaxHighlighter wrapLines={true} language="java" style={theme.dark} mdxType="SyntaxHighlighter">
   {`atlas.notification.embedded=false
   atlas.kafka.zookeeper.connect=<comma separated list of servers forming Zookeeper quorum used by Kafka>
   atlas.kafka.bootstrap.servers=<comma separated list of Kafka broker endpoints in host:port form> - Give at least 2 for redundancy.`}
    </SyntaxHighlighter>
    <h2 {...{
      "id": "known-issues"
    }}>{`Known Issues`}</h2>
    <ul>
      <li parentName="ul">{`If the HBase region servers hosting the Atlas table are down, Atlas would not be able to store or retrieve metadata from HBase until they are brought back online.`}</li>
    </ul>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;